// SYSTEM_ROUTE: /all-systems
import { useMemo, useState } from 'react';
import type { DomainGroup, SystemMetadata } from '@life-os/app-core';
import { CommandBar, MaturityChip, Pane } from '@life-os/ui';
import { useRegistry } from '../app/store.js';
import { useRouter } from '../app/router.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { Icon, iconNameForRoute } from '../app/icons.js';

const GROUP_TITLE: Partial<Record<DomainGroup, string>> = {
  daily: 'Daily',
  direction: 'Direction',
  repair: 'Repair',
  projects: 'Projects',
  knowledge: 'Knowledge',
  study: 'Study',
  career: 'Career',
  health: 'Health',
  finance: 'Finance',
  business: 'Business',
  work: 'Work',
  mental: 'Mental',
  spiritual: 'Spiritual',
  environment: 'Environment',
  assistant: 'Assistant',
  bridges: 'Connections',
  runtime: 'System & Security',
};

export function AllSystemsScreen() {
  const registry = useRegistry();
  const { navigate } = useRouter();
  const [q, setQ] = useState('');

  const all = registry.all();
  const grouped = useMemo(() => {
    const filtered = all.filter(
      (s) =>
        q.trim() === '' ||
        s.label.toLowerCase().includes(q.toLowerCase()) ||
        s.shortPurpose.toLowerCase().includes(q.toLowerCase()),
    );
    const map = new Map<DomainGroup, SystemMetadata[]>();
    for (const s of filtered) {
      const list = map.get(s.group) ?? [];
      list.push(s);
      map.set(s.group, list);
    }
    return [...map.entries()];
  }, [all, q]);

  const matchCount = grouped.reduce((n, [, list]) => n + list.length, 0);

  return (
    <>
      <ScreenHeader
        title="All Systems"
        subtitle="The directory of everything. Nothing hides here."
        actions={<MaturityChip value="daily" />}
      />

      <CommandBar value={q} onChange={setQ} placeholder={`Filter ${all.length} systems…`} shortcutHint="filter" />

      {grouped.length === 0 ? (
        <Pane title="No matches">
          <div style={{ color: 'var(--lc-on-surface-variant)' }}>Nothing matches "{q}". Try another term.</div>
        </Pane>
      ) : (
        grouped.map(([group, systems]) => (
          <Pane key={group} title={GROUP_TITLE[group] ?? group} hint={`${systems.length}`}>
            <div className="hub-grid">
              {systems.map((s) => {
                const route = s.surface.kind === 'route' ? s.surface.path : undefined;
                return (
                  <button
                    key={String(s.id)}
                    className="hub-card"
                    onClick={route ? () => navigate(route) : undefined}
                    disabled={!route}
                  >
                    <span className="hub-card__row">
                      <span className="hub-card__icon">
                        <Icon name={iconNameForRoute(route)} size={20} />
                      </span>
                      <MaturityChip value={s.maturity} />
                    </span>
                    <span className="hub-card__title">{s.label}</span>
                    <span className="hub-card__sub">{s.shortPurpose}</span>
                  </button>
                );
              })}
            </div>
          </Pane>
        ))
      )}

      {q.trim() !== '' && grouped.length > 0 ? (
        <div className="lc-label" style={{ textAlign: 'center', opacity: 0.7 }}>
          {matchCount} of {all.length} systems
        </div>
      ) : null}
    </>
  );
}
